import { useMemo } from 'react';
import { useMatch } from 'react-router';
import useQuery from '@/util/useQuery';
import { fleetPath } from '@/view/pathDefinitions';
import { useEquipmentByIdsQuery } from '../equipment/queries';
import { ConversationBaseDto } from './api.types';
import { useHasChatbotEquipmentPermit } from './useChatbotPermit';

export interface ChatContext extends ChatContextValid {
  equipmentId?: string;
  equipment?: Equipment;
  equipmentMismatch: boolean;
  isContrast: boolean;
}

interface ChatContextProps {
  isOpen: boolean;
  conversation?: ConversationBaseDto;
}

export const useChatContext = ({
  isOpen,
  conversation,
}: ChatContextProps): ChatContext => {
  const { query } = useQuery();
  const isFleet = useIsFleet();
  const equipmentIds = useMemo(
    () => (query.selected && isFleet ? query.selected.split(',') : []),
    [query.selected, isFleet]
  );
  const singleSelectedEquipment =
    equipmentIds.length === 1 ? equipmentIds[0] : undefined;
  const equipmentId = conversation
    ? conversation.equipmentId
    : singleSelectedEquipment;

  const equipmentQuery = useEquipmentByIdsQuery({
    equipmentIds: [equipmentId],
    enabled: isOpen && Boolean(equipmentId),
  });
  const equipment = equipmentQuery.data?.[0];
  const equipmentMismatch =
    equipmentIds.length > 0 &&
    Boolean(equipmentId) &&
    equipmentId !== singleSelectedEquipment;
  const isContrast = equipmentMismatch;

  const { isValid, invalidReason } = useIsValid(
    equipmentQuery.isLoading,
    equipmentIds.length,
    equipment,
    conversation
  );

  return {
    equipmentId,
    equipment,
    isValid,
    invalidReason,
    equipmentMismatch,
    isContrast,
  };
};

const useIsFleet = () => Boolean(useMatch(`${fleetPath}/*`));

type InvalidReason =
  | 'equipment-loading'
  | 'multiple-equipment-selected'
  | 'no-equipment-selected'
  | 'no-permit'
  | 'not-in-fleet';

interface ChatContextValid {
  isValid: boolean;
  invalidReason?: InvalidReason;
}

const useIsValid = (
  equipmentIsLoading: boolean,
  selectionLength: number,
  equipment?: Equipment,
  conversation?: ConversationBaseDto
): ChatContextValid => {
  const { hasPermit, permitIsLoading } =
    useHasChatbotEquipmentPermit(equipment);
  const hasActiveConversation = Boolean(conversation);
  const isFleet = useIsFleet();

  if (equipmentIsLoading || permitIsLoading) {
    return { isValid: false, invalidReason: 'equipment-loading' };
  }

  if (!hasActiveConversation && !isFleet) {
    return { isValid: false, invalidReason: 'not-in-fleet' };
  }

  if (!hasActiveConversation && selectionLength === 0) {
    return { isValid: false, invalidReason: 'no-equipment-selected' };
  }

  if (!hasActiveConversation && selectionLength > 1) {
    return { isValid: false, invalidReason: 'multiple-equipment-selected' };
  }

  if (!hasPermit) {
    return { isValid: false, invalidReason: 'no-permit' };
  }

  return { isValid: true };
};
